import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '../utils/logger';

const AuthContext = createContext();

// Credentials are now stored securely in Netlify environment variables
// No hardcoded credentials in the codebase!

const AUTH_STORAGE_KEY = 'quantdash_auth';

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const authData = localStorage.getItem(AUTH_STORAGE_KEY);
      if (authData) {
        const { loginDate, authenticated, username, displayName } = JSON.parse(authData);
        const today = new Date().toDateString();
        
        // Check if login is from today
        if (authenticated && loginDate === today) {
          setIsAuthenticated(true);
          setCurrentUser(displayName || username || 'Unknown User'); // Prefer displayName, fallback to username
        } else {
          // Session expired (different day)
          localStorage.removeItem(AUTH_STORAGE_KEY);
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      logger.error('❌ Auth: Error checking auth status:', error);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setIsAuthenticated(false);
    }
    
    setIsLoading(false);
  };

  const login = async (username, password) => {
    try {
      // Call secure authentication endpoint
      const response = await fetch('/.netlify/functions/auth-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        const authData = {
          authenticated: true,
          username: data.username,
          displayName: data.displayName,
          loginDate: data.loginDate,
          loginTime: data.loginTime,
          sessionToken: data.sessionToken
        };
        
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
        setIsAuthenticated(true);
        setCurrentUser(data.displayName); // Use displayName instead of username
        
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      logger.error('❌ Auth: Login error:', error);
      return { success: false, error: 'Authentication service unavailable' };
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // Check auth status every hour to handle day changes
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        checkAuthStatus();
      }, 60 * 60 * 1000); // Check every hour

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const value = {
    isAuthenticated,
    currentUser,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
